import { AbstractStorageService, ILocalStorage } from '../../store/utils/contracts/storage.interface';

export class LocalStorageInstance<TModel> extends AbstractStorageService<TModel> {

  constructor(
    private name: string
  ) { super(); }

  // private name: string;
  private predicateNotDefinedError = 'Predicate not defined!';
  private itemsNotDefinedError = 'Items not defined!';
  private itemNotDefinedError = 'Item not defined!';

  every(predicate: (item: TModel) => boolean): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!predicate) {
        reject(new Error(this.predicateNotDefinedError));
      }

      const result = localStorage.getItem(this.name);

      if (result === null) {
        resolve(false);
        return;
      }

      const deserializedResult = JSON.parse(result) as TModel[];

      resolve(deserializedResult.every(predicate));
    });
  }

  some(predicate: (item: TModel) => boolean): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!predicate) {
        reject(new Error(this.predicateNotDefinedError));
      }

      const result = localStorage.getItem(this.name);

      if (result === null) {
        resolve(false);
        return;
      }

      const deserializedResult = JSON.parse(result) as TModel[];

      resolve(deserializedResult.some(predicate));
    });
  }

  find(predicate: (item: TModel) => boolean): Promise<TModel | undefined> {
    return new Promise((resolve, reject) => {
      if (!predicate) {
        reject(new Error(this.predicateNotDefinedError));
      }

      const result = localStorage.getItem(this.name);

      if (result === null) {
        resolve(undefined);
        return;
      }

      const deserializedResult = JSON.parse(result) as TModel[];

      resolve(deserializedResult.find(predicate));
    });
  }

  filter(predicate: (item: TModel) => boolean): Promise<TModel[]> {
    return new Promise((resolve, reject) => {
      if (!predicate) {
        reject(new Error(this.predicateNotDefinedError));
      }

      const result = localStorage.getItem(this.name);

      if (result === null) {
        resolve([]);
        return;
      }

      const deserializedResult = JSON.parse(result) as TModel[];

      resolve(deserializedResult.filter(predicate));
    });
  }

  getAll(): Promise<TModel[]> {
    return new Promise((resolve, reject) => {
      const result = localStorage.getItem(this.name);

      if (result === null) {
        resolve([]);
        return;
      }

      resolve(JSON.parse(result) as TModel[]);
    });
  }

  addOne(item: TModel): Promise<TModel | null> {
    return new Promise((resolve, reject) => {
      const result = localStorage.getItem(this.name);

      let items: TModel[] = [];

      if (result !== null) {
        items = JSON.parse(result) as TModel[];
      }

      items.push(item);

      localStorage.setItem(this.name, JSON.stringify(items));

      resolve(item);
    });
  }

  upsertOne(predicate: (item: TModel) => boolean, item: TModel): Promise<TModel | null> {
    return new Promise((resolve, reject) => {
      if (!predicate) {
        reject(new Error(this.predicateNotDefinedError));
      }

      if (!item) {
        reject(new Error(this.itemNotDefinedError));
      }

      const result = localStorage.getItem(this.name);

      let items: TModel[] = [];

      if (result !== null) {
        items = JSON.parse(result) as TModel[];

        const existingItem = items.find(predicate);
        if (!existingItem) {
          items.push(item);
        } else {
          const index = items.findIndex(predicate);
          items[index] = item;
        }
      } else {
        items.push(item);
      }

      localStorage.setItem(this.name, JSON.stringify(items));

      resolve(item);
    });
  }

  addMany(_items: TModel[]): Promise<TModel[]> {
    return new Promise((resolve, reject) => {
      if (!_items) {
        reject(new Error(this.itemsNotDefinedError));
      }

      const result = localStorage.getItem(this.name);

      let items: TModel[] = [];

      if (result != null) {
        items = JSON.parse(result) as TModel[];
      }

      localStorage.setItem(this.name, JSON.stringify(items));

      items.push(..._items);

      resolve(items);
    });
  }

  removeOne(predicate: (item: TModel) => boolean): Promise<TModel | undefined> {
    return new Promise((resolve, reject) => {
      if (!predicate) {
        reject(new Error(this.predicateNotDefinedError));
      }

      const result = localStorage.getItem(this.name);

      let items: TModel[] = [];

      if (result != null) {
        items = JSON.parse(result) as TModel[];
      } else {
        resolve(undefined);
        return;
      }

      const item = items.find(predicate);
      if (!item) {
        resolve(undefined);
        return;
      }

      localStorage.setItem(this.name, JSON.stringify(items));

      resolve(item);
    });
  }

  removeMany(predicate: (item: TModel) => boolean): Promise<TModel[] | undefined> {
    return new Promise((resolve, reject) => {
      if (!predicate) {
        reject(new Error(this.predicateNotDefinedError));
      }

      const resultFromStorage = localStorage.getItem(this.name);

      let items: TModel[] = [];

      if (resultFromStorage != null) {
        items = JSON.parse(resultFromStorage) as TModel[];
      } else {
        resolve(undefined);
        return;
      }

      const result = items.filter(predicate);
      if (!result) {
        resolve(undefined);
        return;
      }

      const indexes = result.map((v, i) => i);
      const removedItems: TModel[] = [];
      indexes.reverse().forEach(i => {
        removedItems.push(result.splice(i, 1)[0]);
      });

      localStorage.setItem(this.name, JSON.stringify(result));

      resolve(removedItems);
    });
  }

  updateOne(predicate: (item: TModel) => boolean, item: TModel): Promise<TModel | undefined> {
    return new Promise((resolve, reject) => {
      if (!predicate) {
        reject(new Error(this.predicateNotDefinedError));
      }

      if (!item) {
        reject(new Error(this.itemNotDefinedError));
      }

      const resultFromStorage = localStorage.getItem(this.name);

      let items: TModel[] = [];

      if (resultFromStorage != null) {
        items = JSON.parse(resultFromStorage) as TModel[];
      } else {
        resolve(undefined);
        return;
      }

      const _item = items.find(predicate);

      if (!_item) {
        resolve(undefined);
        return;
      }

      const _itemIndex = items.indexOf(_item);
      items[_itemIndex] = item;

      localStorage.setItem(this.name, JSON.stringify(items));

      resolve(item);
    });
  }

  clear(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      localStorage.removeItem(this.name);

      resolve(true);
    });
  }
}


export class LocalStorage implements ILocalStorage {
  private cache: { [name: string]: AbstractStorageService<any> } = {};

  constructor() { }

  getInstance<TModel>(ctor: { new(): TModel }): AbstractStorageService<TModel> {
    const name = ctor.name;
    let instance = this.cache[name];

    if (!instance) {
      this.cache[name] = instance = new LocalStorageInstance<TModel>(name);
    }

    return instance;
  }
}
